import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { DateTime } from "luxon";
import { buildActionColumn, buildAvatarDetailsColumn, buildTagColumn } from "@/components/Table";
import Tag from "@/components/Tag";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import {
  ASSIGNMENT_DISPLAY_STATUS_LABELS,
  ASSIGNMENT_DISPLAY_STATUS_TAG_TYPES,
  getAssignmentDisplayStatus
} from "../assignmentDisplayStatus";
import type { CommitteeAssignmentWithCommittee, CommitteeRow, CommitteeStatus } from "../types";

const COMMITTEE_STATUS_LABELS: Record<CommitteeStatus, string> = {
  ACTIVE: "Active",
  INACTIVE: "Inactive"
};

const MOBILE_ONLY_HIDDEN_CLASS = "max-lg:hidden";

function assignmentDisplay(participant: CommitteeAssignmentWithCommittee) {
  const status = getAssignmentDisplayStatus(participant, DateTime.utc().toISODate() ?? "");
  return {
    label: ASSIGNMENT_DISPLAY_STATUS_LABELS[status],
    tagType: ASSIGNMENT_DISPLAY_STATUS_TAG_TYPES[status]
  };
}

function mobileDetailField(label: string, value: ReactNode) {
  return (
    <div className="text-body-small text-sys-brand-on-surface-variant">
      <span className="font-medium mr-1 whitespace-nowrap">{label}:</span>
      <span>{value}</span>
    </div>
  );
}

function statusColumn<T extends { status: CommitteeStatus }>(): ColumnDef<T, unknown> {
  return buildTagColumn<T>({
    accessorKey: "status",
    header: "Status",
    getTagLabel: (row) => COMMITTEE_STATUS_LABELS[row.status],
    getTagType: (row) => (row.status === "ACTIVE" ? "success" : "surface"),
    getTagStyle: () => "strong",
    sortType: "text",
    size: 120
  });
}

const committeeNameColumn: ColumnDef<CommitteeRow, unknown> = {
  accessorKey: "name",
  header: "Committee Name",
  size: 280,
  meta: {
    sortType: "text",
    headerClassName: "hide-header-in-column"
  }
};

function participantName(participant: CommitteeAssignmentWithCommittee) {
  return participant.person?.displayName ?? "Unknown Participant";
}

function participantPgaId(participant: CommitteeAssignmentWithCommittee) {
  return participant.person?.pgaId ?? "";
}

export function canViewCommitteeAssignmentPerson(participant: CommitteeAssignmentWithCommittee): boolean {
  return participant.person != null && !!participant.person.pgaId;
}

function assignmentMobileDetailFields(participant: CommitteeAssignmentWithCommittee) {
  const { label, tagType } = assignmentDisplay(participant);

  return (
    <div className="flex w-full flex-col items-start gap-1">
      {mobileDetailField("Position", participant.title.title)}
      {mobileDetailField("Term Start Date", participant.startDate ? toFormat(participant.startDate) : "--")}
      {mobileDetailField("Term End Date", participant.endDate ? toFormat(participant.endDate) : "--")}
      <div className="w-fit">
        <Tag tagType={tagType} size="small" tagStyle="strong">
          {label}
        </Tag>
      </div>
    </div>
  );
}

function assignmentDetailsColumns(isCompactLayout: boolean): ColumnDef<CommitteeAssignmentWithCommittee, unknown>[] {
  return buildAvatarDetailsColumn({
    isCompactLayout,
    getTitle: participantName,
    getSubtitle: participantPgaId,
    subtitleLabel: "PGA ID",
    getPicture: (row) => row.person?.profilePhoto ?? undefined,
    header: "Participant",
    additionalMobileFields: isCompactLayout ? assignmentMobileDetailFields : undefined,
    id: "participant"
  });
}

export type CatalogColumnsOptions = {
  canManage?: boolean;
  setMenu?: (menu: TableRowMenu) => void;
  onAddPeople?: (row: CommitteeRow) => void;
  onEdit?: (row: CommitteeRow) => void;
  onStatusChange?: (row: CommitteeRow) => void;
};

function participationCountColumn(
  accessorKey: "activeParticipantCount" | "inactiveParticipantCount" | "totalParticipantCount",
  header: string,
  size: number
): ColumnDef<CommitteeRow, unknown> {
  return {
    accessorKey,
    header,
    size,
    meta: {
      sortType: "count",
      headerClassName: "max-sm:inline"
    }
  };
}

export function getCatalogColumns(options: CatalogColumnsOptions): ColumnDef<CommitteeRow, unknown>[] {
  const { canManage = false, setMenu, onAddPeople, onEdit, onStatusChange } = options;

  const columns: ColumnDef<CommitteeRow, unknown>[] = [
    committeeNameColumn,
    {
      accessorKey: "core",
      header: "Core",
      cell: ({ row }) => (row.original.core ? "Yes" : "No"),
      size: 100,
      meta: { sortType: "text" }
    },
    participationCountColumn("activeParticipantCount", "Active Participations", 160),
    participationCountColumn("inactiveParticipantCount", "Inactive Participations", 170),
    participationCountColumn("totalParticipantCount", "Total Participations", 160),
    statusColumn<CommitteeRow>()
  ];

  if (canManage && setMenu && onAddPeople && onEdit && onStatusChange) {
    columns.push(
      buildActionColumn<CommitteeRow>({
        getActions: (row) => {
          const actions = [
            { text: "Add People", onClick: () => onAddPeople(row) },
            { text: "Edit Committee", onClick: () => onEdit(row) }
          ];

          if (row.status === "ACTIVE") {
            actions.push({ text: "Deactivate Committee", onClick: () => onStatusChange(row) });
          } else {
            actions.push({ text: "Activate Committee", onClick: () => onStatusChange(row) });
          }

          return actions;
        },
        actionId,
        setMenu,
        getRowId: (row) => row.id
      })
    );
  }

  return columns;
}

export function getCommitteeAssignmentDetailsColumns(
  isCompactLayout: boolean,
  onViewParticipant: (participant: CommitteeAssignmentWithCommittee) => void,
  options?: {
    canManage?: boolean;
    onEditParticipant?: (participant: CommitteeAssignmentWithCommittee) => void;
  }
): ColumnDef<CommitteeAssignmentWithCommittee, unknown>[] {
  const compactOnlyHiddenClass = isCompactLayout ? MOBILE_ONLY_HIDDEN_CLASS : undefined;
  const { canManage = false, onEditParticipant } = options ?? {};

  return [
    ...assignmentDetailsColumns(isCompactLayout),
    {
      accessorKey: "title",
      header: "Position",
      cell: ({ row }) => row.original.title.title,
      enableSorting: false,
      meta: { className: compactOnlyHiddenClass }
    },
    {
      accessorKey: "startDate",
      header: "Term Start Date",
      cell: ({ row }) => (row.original.startDate ? toFormat(row.original.startDate) : "--"),
      enableSorting: false,
      meta: { className: compactOnlyHiddenClass }
    },
    {
      accessorKey: "endDate",
      header: "Term End Date",
      cell: ({ row }) => (row.original.endDate ? toFormat(row.original.endDate) : "--"),
      enableSorting: false,
      meta: { className: compactOnlyHiddenClass }
    },
    {
      ...buildTagColumn<CommitteeAssignmentWithCommittee>({
        accessorKey: "status",
        header: "Status",
        getTagLabel: (row) => assignmentDisplay(row).label,
        getTagType: (row) => assignmentDisplay(row).tagType,
        getTagStyle: () => "strong",
        enableSorting: false,
        meta: { className: compactOnlyHiddenClass }
      }),
      accessorFn: (row) => assignmentDisplay(row).label
    },
    buildActionColumn<CommitteeAssignmentWithCommittee>({
      onViewClick: onViewParticipant,
      showViewButton: canViewCommitteeAssignmentPerson,
      isRowNavigable: canViewCommitteeAssignmentPerson,
      viewButtonTitle: "View participant details",
      onEditClick: canManage && onEditParticipant ? onEditParticipant : undefined,
      editButtonTitle: "Edit participation",
      actionId: (rowId) => `committee-assignment-action-${rowId}`,
      setMenu: () => undefined
    })
  ];
}

export const CATALOG_SORT_FIELD_MAP: Record<string, "NAME" | "STATUS" | "CORE"> = {
  name: "NAME",
  status: "STATUS",
  core: "CORE"
};
